import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Smile, Frown, UserCheck } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

export const SecurityUsability = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const [balance, setBalance] = useState(50); // 0 (Usable) to 100 (Secure)
    const [gameStatus, setGameStatus] = useState<'playing' | 'hacked' | 'churned' | 'win'>('playing');

    // Stats
    const securityScore = balance;
    const usabilityScore = 100 - balance;

    const policies = [
        { val: 0, label: "No Password", icon: "🔓" },
        { val: 25, label: "Simple Password (4 chars)", icon: "🔑" },
        { val: 50, label: "Strong Pwd + MFA (SMS)", icon: "📱" },
        { val: 75, label: "Pwd + Hardware Key + Biometric", icon: "🧬" },
        { val: 100, label: "DNA Sample + Hourly Rotation", icon: "☣️" },
    ];

    const currentPolicy = policies.reduce((prev, curr) =>
        Math.abs(curr.val - balance) < Math.abs(prev.val - balance) ? curr : prev
    );

    const submitConfig = () => {
        if (balance < 30) {
            setGameStatus('hacked');
        } else if (balance > 70) {
            setGameStatus('churned');
        } else {
            setGameStatus('win');
            if (!completed) {
                setCompleted(true);
                completeModule('security-usability', 100);
                setTimeout(() => setShowSuccess(true), 1500);
            }
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100 pt-24">
            <div className="max-w-4xl mx-auto p-8">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-black font-comic text-yellow-500 mb-4 flex items-center justify-center gap-3">
                        <UserCheck size={40} /> The Balance
                    </h1>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                        Security is a trade-off. Too strict, and users leave. Too loose, and hackers enter.
                    </p>
                </div>

                <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700 shadow-2xl">

                    {/* Visualizer */}
                    <div className="flex justify-between items-end h-64 mb-8 bg-slate-900 rounded-2xl p-8 relative overflow-hidden">

                        {/* Users (Usability) */}
                        <div className="flex flex-col items-center gap-2 z-10 w-1/3">
                            <motion.div
                                animate={{ scale: usabilityScore / 50 }}
                                className="text-yellow-400"
                            >
                                {usabilityScore > 50 ? <Smile size={64} /> : <Frown size={64} />}
                            </motion.div>
                            <div className="font-bold text-yellow-400">User Happiness</div>
                            <div className="h-4 w-full bg-slate-700 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full bg-yellow-400"
                                    animate={{ width: `${usabilityScore}%` }}
                                />
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="h-full w-1 bg-slate-700 mx-4 z-10"></div>

                        {/* Shield (Security) */}
                        <div className="flex flex-col items-center gap-2 z-10 w-1/3">
                            <motion.div
                                animate={{ scale: securityScore / 50 }}
                                className="text-blue-400"
                            >
                                <Shield size={64} />
                            </motion.div>
                            <div className="font-bold text-blue-400">System Security</div>
                            <div className="h-4 w-full bg-slate-700 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full bg-blue-400"
                                    animate={{ width: `${securityScore}%` }}
                                />
                            </div>
                        </div>

                        {/* Background Context */}
                        {gameStatus === 'hacked' && (
                            <div className="absolute inset-0 bg-red-900/50 flex items-center justify-center pointer-events-none">
                                <h1 className="text-6xl font-black text-red-500 rotate-12 opacity-50">HACKED</h1>
                            </div>
                        )}
                        {gameStatus === 'churned' && (
                            <div className="absolute inset-0 bg-yellow-900/50 flex items-center justify-center pointer-events-none">
                                <h1 className="text-6xl font-black text-yellow-500 -rotate-12 opacity-50">NO USERS</h1>
                            </div>
                        )}

                    </div>

                    {/* Controls */}
                    <div className="space-y-6">
                        <div className="text-center text-3xl mb-4 font-bold text-white">
                            Policy: <span className="text-purple-400">{currentPolicy.label}</span> {currentPolicy.icon}
                        </div>

                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={balance}
                            onChange={(e) => {
                                setBalance(parseInt(e.target.value));
                                setGameStatus('playing');
                            }}
                            className="w-full h-4 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                        />
                        <div className="flex justify-between text-xs text-slate-500 font-bold uppercase tracking-widest">
                            <span>Maximum Usability</span>
                            <span>Sweet Spot?</span>
                            <span>Maximum Security</span>
                        </div>

                        <button
                            onClick={submitConfig}
                            className="w-full py-4 rounded-xl font-black text-lg bg-white text-slate-900 hover:bg-slate-200 transition-all shadow-lg active:scale-95"
                        >
                            DEPLOY POLICY
                        </button>
                    </div>

                    {/* Feedback */}
                    {gameStatus === 'hacked' && (
                        <div className="mt-6 p-4 bg-red-900/30 border border-red-500/50 rounded-xl text-red-200 text-center">
                            Your users are happy, but you got breached immediately. Weak passwords!
                        </div>
                    )}
                    {gameStatus === 'churned' && (
                        <div className="mt-6 p-4 bg-yellow-900/30 border border-yellow-500/50 rounded-xl text-yellow-200 text-center">
                            Your system is secure, but nobody uses it because logging in takes 10 minutes.
                        </div>
                    )}
                </div>
            </div>

            {showSuccess && (
                <ModuleSuccess
                    title="Perfect Balance!"
                    onRetry={() => setShowSuccess(false)}
                >
                    <div className="space-y-4">
                        <p className="text-slate-600">
                            You found the Sweet Spot!
                        </p>
                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                            <h4 className="font-bold text-blue-800 text-sm mb-1">Principle: Friction vs Safety</h4>
                            <p className="text-blue-700 text-xs">
                                Good security (like biometric or seamless MFA) adds safety without annoying the user. "The best security is invisible."
                            </p>
                        </div>
                    </div>
                </ModuleSuccess>
            )}
        </div>
    );
};
